import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { bypassedApisWithoutEndpoint } from 'src/app/utility/static-data/bypassed-apis';
import { getTokenFromStorage } from 'src/app/utility/common-logic';

const CACHE_TTL = 1000 * 60 * 10;

interface CacheEntry {
  data: any;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private cache = new Map<string, CacheEntry>();
  defaultLanguage = environment.settings.defaultLanguage;
  debug = environment.settings.debug;

  constructor(private http: HttpClient) {}

  get lang() {
    const localLang = localStorage.getItem('lang');
    return !localLang ? this.defaultLanguage : localLang;
  }

  private _buildHeaders(
    serviceName: string,
    ignoreJsonContentType = false,
    multipartFormData = false,
  ): HttpHeaders {
    const headers: { [key: string]: string } = { 'Accept-Language': this.lang };
    if (!ignoreJsonContentType && !multipartFormData) {
      headers['Content-Type'] = 'application/json';
    }
    const token = getTokenFromStorage();
    if (
      token &&
      !bypassedApisWithoutEndpoint.some((x) => serviceName.startsWith(x))
    ) {
      headers['Authorization'] = `Token ${token}`;
    }
    return new HttpHeaders(headers);
  }

  private _wrapError(err: unknown): never {
    if (err instanceof HttpErrorResponse) {
      throw {
        response: {
          status: err.status,
          data: err.error,
          statusText: err.statusText,
        },
        message: err.message,
      };
    }
    throw err;
  }

  private _pruneCache(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache) {
      if (now >= entry.expiresAt) {
        this.cache.delete(key);
      }
    }
  }

  async get(
    serviceName: string,
    cacheData?: { cache?: boolean; initiateApiCall?: boolean },
    id?: string,
  ) {
    const headers = this._buildHeaders(serviceName);
    const url = environment.url.app + serviceName;

    if (cacheData?.cache && !cacheData?.initiateApiCall) {
      const cacheKey = id ? `${url}__${id}` : url;
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() < cached.expiresAt) {
        return cached.data;
      }
      try {
        const data = await firstValueFrom(this.http.get<any>(url, { headers }));
        const result = { data };
        this._pruneCache();
        this.cache.set(cacheKey, {
          data: result,
          expiresAt: Date.now() + CACHE_TTL,
        });
        return result;
      } catch (err) {
        this._wrapError(err);
      }
    } else {
      try {
        const data = await firstValueFrom(this.http.get<any>(url, { headers }));
        return { data };
      } catch (err) {
        this._wrapError(err);
      }
    }
  }

  async post(
    serviceName: string,
    data: any,
    ignoreJsonContentType = false,
    multipartFormData = false,
  ) {
    const headers = this._buildHeaders(
      serviceName,
      ignoreJsonContentType,
      multipartFormData,
    );
    const url = environment.url.app + serviceName;
    try {
      const responseData = await firstValueFrom(
        this.http.post<any>(url, data, { headers }),
      );
      return { data: responseData };
    } catch (err) {
      this._wrapError(err);
    }
  }

  async put(
    serviceName: string,
    data: any,
    ignoreJsonContentType = false,
    multipartFormData = false,
  ) {
    const headers = this._buildHeaders(
      serviceName,
      ignoreJsonContentType,
      multipartFormData,
    );
    const url = environment.url.app + serviceName;
    try {
      const responseData = await firstValueFrom(
        this.http.put<any>(url, data, { headers }),
      );
      return { data: responseData };
    } catch (err) {
      this._wrapError(err);
    }
  }

  async delete(serviceName: string, postMethod = true, data: any = null) {
    const headers = this._buildHeaders(serviceName);
    const url = environment.url.app + serviceName;
    try {
      if (postMethod) {
        const responseData = await firstValueFrom(
          this.http.post<any>(url, null, { headers }),
        );
        return { data: responseData };
      } else if (data) {
        const responseData = await firstValueFrom(
          this.http.delete<any>(url, { headers, body: data }),
        );
        return { data: responseData };
      } else {
        const responseData = await firstValueFrom(
          this.http.delete<any>(url, { headers }),
        );
        return { data: responseData };
      }
    } catch (err) {
      this._wrapError(err);
    }
  }

  async patch(
    serviceName: string,
    patchData: any,
    ignoreJsonContentType = false,
    multipartFormData = false,
  ) {
    const headers = this._buildHeaders(
      serviceName,
      ignoreJsonContentType,
      multipartFormData,
    );
    const url = environment.url.app + serviceName;
    try {
      const responseData = await firstValueFrom(
        this.http.patch<any>(url, patchData, { headers }),
      );
      return { data: responseData };
    } catch (err) {
      this._wrapError(err);
    }
  }

  clearStorage() {
    this.cache.clear();
  }
}
